import { Map, fromJS } from 'immutable'
import { compose, isNil } from 'ramda'
import { get, getEq, find } from 'utils/immutable'

// Bug 154153: SIG00085 (Utility Light Switch) ships with released/pressed API labels
// but must display as off/on. Override translations at read-time; all other metadata is preserved.
const SIG00085_ID = 'SIG00085'
const SIG00085_TRANSLATIONS = fromJS([
  { value: 0, translation: 'off' },
  { value: 1, translation: 'on' },
])

// Returns true only when the translations list carries exactly the known-bad two-state
// mapping: value 0 → 'released' and value 1 → 'pressed'. Matched by value (not index)
// so order in the list does not matter.
const isKnownBadMapping = (translations) => {
  if (!translations || translations.size !== 2) return false
  const byValue = translations.reduce(
    (acc, entry) => acc.set(entry.get('value'), entry.get('translation')),
    Map()
  )
  return byValue.get(0) === 'released' && byValue.get(1) === 'pressed'
}

/**
 * Gets the translation info for a signal
 * @param {Map} signal
 * @param {String} inputConfig // optional
 * @returns {Map}
 *
 * @example
 *    const booleanSignal = {
 *       ...,
 *       translation: {
 *         defaultValue: 0,
 *         maxValue: 65534,
 *         minValue: 0,
 *         translations: [{ value: 0, translation: "off" }, { value: 1, translation: "on" }],
 *       },
 *       translationType: "boolean",
 *     }
 *
 *     findTranslations(booleanSignal) // => { defaultValue: 0, maxValue: 65535, ... }
 *
 * @example
 *     const multipleRangeSignal = {
 *        ...,
 *        inputTranslations: [
 *          { inputConfig: VOLT_0_6, translation: { ... }},
 *          ...
 *        ]
 *     }
 *
 *     findTranslations(multipleRangeSignal, VOLT_0_6) // => translation
 */
const findTranslations = (signal, inputConfig = null) => {
  if (!signal || signal.isEmpty()) return Map()

  if (!Map.isMap(signal)) {
    throw new Error('signal must be of type `Map`')
  }

  const getTranslation = get('translation')
  const inputTranslations = get('inputTranslations', signal)

  if (isNil(inputTranslations)) {
    const translation = getTranslation(signal)
    // Override off/on labels for SIG00085 only when API returns the known-bad released/pressed mapping
    if (
      get('id', signal) === SIG00085_ID &&
      Map.isMap(translation) &&
      isKnownBadMapping(translation.get('translations'))
    ) {
      return translation.set('translations', SIG00085_TRANSLATIONS)
    }
    return translation
  }

  if (isNil(inputConfig)) {
    throw new Error('inputConfig is not defined')
  }

  const getInputTranslation = compose(
    getTranslation,
    find(getEq('inputConfig', inputConfig))
  )
  return getInputTranslation(inputTranslations) || Map()
}

export default findTranslations
